import React, { useState, useEffect } from "react";
import {
    Card,
    Grid,
    Icon,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Checkbox,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import MDBadge from "components/MDBadge";
import { get, postNoResponse, getFile } from "utils/WebService";
import moment from "moment";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { infoAviso } from "utils/Functions";

export default function BaixaPagamentoConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Search and results states
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [devedores, setDevedores] = useState([]);
    const [selectedDevedor, setSelectedDevedor] = useState(null);

    // Show canceled agreements
    const [showCanceledAcordos, setShowCanceledAcordos] = useState(false);

    // Tab state: "acordos" or "contratos"
    const [activeTab, setActiveTab] = useState("acordos");

    // Selection states
    // Stores selected item IDs mapping to their full item object
    const [selectedItems, setSelectedItems] = useState({});

    // Batch Baixa Dialog states
    const [baixaDialogOpen, setBaixaDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [baixaDataPagamento, setBaixaDataPagamento] = useState(moment().format("YYYY-MM-DD"));
    const [baixaFormaPagamento, setBaixaFormaPagamento] = useState("Boleto");
    const [baixaForm, setBaixaForm] = useState({}); // key: itemId, value: { valorPago, valorDesconto, valorAcrescimo, title, balance }

    // Snackbar alerts
    const [sbMessage, setSbMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const handleSearch = () => {
        if (!searchTerm) {
            showSnackbar("Digite um ID, Código do Cliente, CPF/CNPJ ou Nome.", false);
            return;
        }
        setLoading(true);
        setSelectedDevedor(null);
        setSelectedItems({});
        setShowCanceledAcordos(false);
        get(`/acordo/parcela/search?query=${encodeURIComponent(searchTerm)}`)
            .then(res => {
                setDevedores(res || []);
                if (!res || res.length === 0) {
                    showSnackbar("Nenhum devedor com pendências encontrado.", false);
                } else if (res.length === 1) {
                    handleSelectDevedor(res[0]);
                }
            })
            .catch(err => showSnackbar(err.message || "Erro na busca", false))
            .finally(() => setLoading(false));
    };

    const handleSelectDevedor = (devedor) => {
        setSelectedDevedor(devedor);
        setSelectedItems({});
        setShowCanceledAcordos(false);
        // Default to "acordos" if has acordos, else "contratos"
        if (devedor.acordos && devedor.acordos.length > 0) {
            setActiveTab("acordos");
        } else {
            setActiveTab("contratos");
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedItems({}); // Clear selection on tab change to avoid mixing types
    };

    // Toggle single item selection
    const handleToggleItem = (itemId, item, isContrato, acordo = null) => {
        const newSelected = { ...selectedItems };
        if (newSelected[itemId]) {
            delete newSelected[itemId];
        } else {
            let title = "";
            if (isContrato) {
                title = item.titulo;
            } else {
                const acordoSuffix = acordo ? ` (Acordo ${acordo.idAcordo || acordo.id})` : "";
                title = `Parcela ${item.numeroParcela}${acordoSuffix}`;
            }
            newSelected[itemId] = {
                id: itemId,
                title: title,
                balance: isContrato ? item.saldo : item.valorParcela
            };
        }
        setSelectedItems(newSelected);
    };

    // Toggle select all for Acordos (per agreement)
    const handleToggleSelectAllAcordo = (acordo, checked) => {
        const newSelected = { ...selectedItems };
        if (acordo.parcelas) {
            acordo.parcelas.forEach(p => {
                // Only select pending installments (situacao = 1 (Aberto) or 3 (Atrasado))
                if (p.situacao === 1 || p.situacao === 3) {
                    if (checked) {
                        newSelected[p.id] = {
                            id: p.id,
                            title: `Parcela ${p.numeroParcela} (Acordo ${acordo.idAcordo || acordo.id})`,
                            balance: p.valorParcela
                        };
                    } else {
                        delete newSelected[p.id];
                    }
                }
            });
        }
        setSelectedItems(newSelected);
    };

    // Toggle select all for Contratos
    const handleToggleSelectAllContratos = (checked) => {
        const newSelected = { ...selectedItems };
        if (selectedDevedor && activeContratos) {
            activeContratos.forEach(c => {
                const isPending = c.situacao === null || c.situacao === undefined || c.situacao === 1 || c.situacao === 2;
                if (isPending) {
                    if (checked) {
                        newSelected[c.id] = {
                            id: c.id,
                            title: c.titulo,
                            balance: c.saldo
                        };
                    } else {
                        delete newSelected[c.id];
                    }
                }
            });
        }
        setSelectedItems(newSelected);
    };

    // Open batch write-off dialog
    const handleOpenBaixaDialog = () => {
        const keys = Object.keys(selectedItems);
        if (keys.length === 0) return;

        const initialForm = {};
        keys.forEach(key => {
            const item = selectedItems[key];
            initialForm[key] = {
                valorPago: item.balance,
                valorDesconto: 0,
                valorAcrescimo: 0,
                title: item.title,
                balance: item.balance
            };
        });

        setBaixaForm(initialForm);
        setBaixaDataPagamento(moment().format("YYYY-MM-DD"));
        setBaixaFormaPagamento("Boleto");
        setBaixaDialogOpen(true);
    };

    const handleCloseBaixaDialog = () => {
        setBaixaDialogOpen(false);
        setBaixaForm({});
        setBaixaFormaPagamento("Boleto");
    };

    const handleFormValueChange = (itemId, field, value) => {
        setBaixaForm(prev => {
            const currentItem = prev[itemId];
            if (!currentItem) return prev;

            const updatedItem = { ...currentItem, [field]: value };

            if (field === "valorPago") {
                if (value === "") {
                    updatedItem.valorDesconto = "";
                    updatedItem.valorAcrescimo = "";
                } else {
                    const balance = parseFloat(currentItem.balance || 0);
                    const valorPago = parseFloat(value || 0);
                    if (isNaN(valorPago)) {
                        updatedItem.valorDesconto = 0;
                        updatedItem.valorAcrescimo = 0;
                    } else if (valorPago < balance) {
                        updatedItem.valorDesconto = parseFloat((balance - valorPago).toFixed(2));
                        updatedItem.valorAcrescimo = 0;
                    } else if (valorPago > balance) {
                        updatedItem.valorDesconto = 0;
                        updatedItem.valorAcrescimo = parseFloat((valorPago - balance).toFixed(2));
                    } else {
                        updatedItem.valorDesconto = 0;
                        updatedItem.valorAcrescimo = 0;
                    }
                }
            } else if (field === "valorDesconto") {
                const balance = parseFloat(currentItem.balance || 0);
                const desc = parseFloat(value || 0);
                const acres = parseFloat(currentItem.valorAcrescimo || 0);
                updatedItem.valorPago = parseFloat((balance - desc + acres).toFixed(2));
            } else if (field === "valorAcrescimo") {
                const balance = parseFloat(currentItem.balance || 0);
                const desc = parseFloat(currentItem.valorDesconto || 0);
                const acres = parseFloat(value || 0);
                updatedItem.valorPago = parseFloat((balance - desc + acres).toFixed(2));
            }

            return {
                ...prev,
                [itemId]: updatedItem
            };
        });
    };

    const handleConfirmBaixa = () => {
        const keys = Object.keys(baixaForm);
        if (keys.length === 0 || !baixaDataPagamento) return;

        setLoading(true);

        const itemsPayload = keys.map(key => {
            const f = baixaForm[key];
            return {
                id: key,
                valorPago: parseFloat(f.valorPago?.toString().replace(",", ".") || "0"),
                valorDesconto: parseFloat(f.valorDesconto?.toString().replace(",", ".") || "0"),
                valorAcrescimo: parseFloat(f.valorAcrescimo?.toString().replace(",", ".") || "0")
            };
        });

        const payload = {
            itens: itemsPayload,
            isContrato: activeTab === "contratos",
            dataPagamento: baixaDataPagamento,
            formaPagamento: baixaFormaPagamento
        };

        postNoResponse("/acordo/parcela/baixar-lote", payload)
            .then(() => {
                showSnackbar("Baixa(s) realizada(s) com sucesso!", true);
                setConfirmDialogOpen(false);
                handleCloseBaixaDialog();
                reloadDevedorData();
            })
            .catch(err => {
                showSnackbar(err.message || "Erro ao realizar baixa.", false);
                setConfirmDialogOpen(false);
                setLoading(false);
            });
    };

    const reloadDevedorData = () => {
        if (searchTerm) {
            get(`/acordo/parcela/search?query=${encodeURIComponent(searchTerm)}`)
                .then(res => {
                    setDevedores(res || []);
                    if (res && res.length > 0) {
                        const prevSelected = res.find(d => d.idDevedor === selectedDevedor?.idDevedor);
                        if (prevSelected) {
                            setSelectedDevedor(prevSelected);
                        } else {
                            setSelectedDevedor(res[0]);
                        }
                    } else {
                        setSelectedDevedor(null);
                    }
                    setSelectedItems({});
                })
                .catch(() => setSelectedDevedor(null))
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    };

    const handleEstornar = (itemId, isContrato) => {
        infoAviso(
            "Deseja realmente estornar a baixa deste item? Esta ação reabrirá o saldo/parcela e registrará a ocorrência no histórico.",
            () => {
                setLoading(true);
                postNoResponse("/acordo/parcela/estornar", { id: itemId, isContrato })
                    .then(() => {
                        showSnackbar("Baixa estornada com sucesso!", true);
                        reloadDevedorData();
                    })
                    .catch(err => {
                        console.error(err);
                        showSnackbar(err.message || "Erro ao estornar a baixa.", false);
                        setLoading(false);
                    });
            }
        );
    };

    const handleEmitirBoleto = (idParcela) => {
        setLoading(true);
        getFile(`/acordo/parcela/boleto/${idParcela}`, "boleto.pdf")
            .then(() => {
                showSnackbar("Boleto gerado com sucesso!", true);
            })
            .catch(err => {
                showSnackbar(err.message || "Erro ao gerar boleto.", false);
            })
            .finally(() => setLoading(false));
    };

    // Calculate totals for batch dialog
    const calculateTotals = () => {
        let totalOriginal = 0;
        let totalPago = 0;
        let totalDesconto = 0;
        let totalAcrescimo = 0;

        Object.values(baixaForm).forEach(f => {
            const orig = parseFloat(f.balance);
            const vp = parseFloat(f.valorPago?.toString().replace(",", "."));
            const desc = parseFloat(f.valorDesconto?.toString().replace(",", "."));
            const acres = parseFloat(f.valorAcrescimo?.toString().replace(",", "."));

            totalOriginal += isNaN(orig) ? 0 : orig;
            totalPago += isNaN(vp) ? 0 : vp;
            totalDesconto += isNaN(desc) ? 0 : desc;
            totalAcrescimo += isNaN(acres) ? 0 : acres;
        });

        return { totalOriginal, totalPago, totalDesconto, totalAcrescimo };
    };

    // Filter active and non-returned contracts
    const activeContratos = selectedDevedor?.contratos 
        ? selectedDevedor.contratos.filter(c => c.situacao === null || c.situacao === undefined || c.situacao === 1 || c.situacao === 2 || c.situacao === 3)
        : [];

    const activeAcordosCount = selectedDevedor?.acordos 
        ? selectedDevedor.acordos.filter(a => a.situacao === 1).length
        : 0;

    const activeContratosCount = selectedDevedor?.contratos
        ? selectedDevedor.contratos.filter(c => c.situacao === null || c.situacao === undefined || c.situacao === 1 || c.situacao === 2).length
        : 0;

    const totals = calculateTotals();
    const selectedCount = Object.keys(selectedItems).length;

    return (
        <Grid container display="flex" justifyContent="center" spacing={2} sx={{ p: 1 }}>
            {/* Search Card */}
            <Grid item xs={12}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        background: darkMode ? "#1a1a2e" : "#ffffff",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                        Baixa de Pagamentos
                    </MDTypography>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={12} sm={9} md={10}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Buscar Devedor</InputLabel>
                            <MDInput
                                size="small"
                                fullWidth
                                placeholder="Digite o ID, Código Cliente, CPF, CNPJ ou Nome..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={2}>
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="medium"
                                fullWidth
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                <Icon sx={{ mr: 0.5 }}>search</Icon> Pesquisar
                            </MDButton>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            {/* Multiple Devedores Results Selection List */}
            {devedores.length > 1 && !selectedDevedor && (
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 3, borderColor: darkMode ? "#333" : "#ddd" }}>
                        <MDTypography variant="h6" mb={2} color="info">
                            Selecione um Devedor ({devedores.length} encontrados)
                        </MDTypography>
                        <Grid container spacing={2}>
                            {devedores.map(dev => (
                                <Grid item xs={12} sm={6} md={4} key={dev.idDevedor}>
                                    <MDBox
                                        p={2}
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor={darkMode ? "#444" : "#ddd"}
                                        bgcolor={darkMode ? "#22223b" : "#f8f9fa"}
                                        onClick={() => handleSelectDevedor(dev)}
                                        sx={{
                                            cursor: "pointer",
                                            transition: "0.2s",
                                            "&:hover": {
                                                borderColor: "#2196F3",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0px 4px 10px rgba(33, 150, 243, 0.15)"
                                            }
                                        }}
                                    >
                                        <MDTypography variant="subtitle2" fontWeight="bold">
                                            {dev.nomeDevedor}
                                        </MDTypography>
                                        <MDTypography variant="caption" display="block" color="text">
                                            Doc: {dev.documentoDevedor}
                                        </MDTypography>
                                        <MDTypography variant="caption" display="block" color="text">
                                            Cliente: {dev.codigoCliente}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </Grid>
            )}

            {/* Devedor Details and Financials Display */}
            {selectedDevedor && (
                <Grid item xs={12}>
                    {/* Devedor Header Card */}
                    <Card
                        variant="outlined"
                        sx={{
                            px: 2,
                            py: 1.5,
                            mb: 1.5,
                            background: darkMode ? "#1c1c30" : "#fbfbfb",
                            borderColor: darkMode ? "#333" : "#eee",
                        }}
                    >
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} md={8}>
                                <MDTypography variant="h6" fontWeight="bold" color="info">
                                    {selectedDevedor.nomeDevedor}
                                </MDTypography>
                                <MDBox display="flex" flexWrap="wrap" gap={2} mt={0.5}>
                                    <MDTypography variant="caption" fontWeight="regular" color="text">
                                        <strong>Documento:</strong> {selectedDevedor.documentoDevedor}
                                    </MDTypography>
                                    <MDTypography variant="caption" fontWeight="regular" color="text">
                                        <strong>Código Cliente:</strong> {selectedDevedor.codigoCliente}
                                    </MDTypography>
                                </MDBox>
                            </Grid>
                            {devedores.length > 1 && (
                                <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
                                    <MDButton variant="outlined" color="secondary" size="small" onClick={() => setSelectedDevedor(null)}>
                                        <Icon>swap_horiz</Icon> Alterar Devedor
                                    </MDButton>
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Tabs for Acordo vs Contratos */}
                        <MDBox sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <Tabs value={activeTab} onChange={handleTabChange} aria-label="baixa-abas">
                                <Tab
                                    label={`Acordos (${activeAcordosCount})`}
                                    value="acordos"
                                    sx={{ fontSize: 12, fontWeight: "bold" }}
                                />
                                <Tab
                                    label={`Contratos (${activeContratosCount})`}
                                    value="contratos"
                                    sx={{ fontSize: 12, fontWeight: "bold" }}
                                />
                            </Tabs>
                        </MDBox>

                        {/* Acordos Content Area */}
                        {activeTab === "acordos" && (
                            <MDBox mt={2}>
                                <MDBox display="flex" justifyContent="flex-end" mb={1.5}>
                                    <MDButton
                                        variant="outlined"
                                        color={showCanceledAcordos ? "secondary" : "info"}
                                        size="small"
                                        onClick={() => setShowCanceledAcordos(!showCanceledAcordos)}
                                    >
                                        <Icon sx={{ mr: 0.5 }}>{showCanceledAcordos ? "visibility_off" : "visibility"}</Icon>
                                        {showCanceledAcordos ? "Ocultar Cancelados" : "Mostrar Cancelados"}
                                    </MDButton>
                                </MDBox>
                                {(!selectedDevedor.acordos || 
                                  selectedDevedor.acordos.filter(acordo => showCanceledAcordos || acordo.situacao === 1).length === 0) ? (
                                    <MDTypography variant="button" color="text" display="block" py={2} textAlign="center">
                                        Nenhum acordo {showCanceledAcordos ? "" : "ativo "}encontrado para este devedor.
                                    </MDTypography>
                                ) : (
                                    selectedDevedor.acordos
                                        .filter(acordo => showCanceledAcordos || acordo.situacao === 1)
                                        .map(acordo => {
                                            const pendingParcelas = acordo.parcelas ? acordo.parcelas.filter(p => p.situacao === 1 || p.situacao === 3) : [];
                                            const selectedInAcordo = pendingParcelas.filter(p => selectedItems[p.id]);
                                            const allSelected = pendingParcelas.length > 0 && selectedInAcordo.length === pendingParcelas.length;
                                            const someSelected = selectedInAcordo.length > 0 && !allSelected;

                                            return (
                                                <Card
                                                    key={acordo.id}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        mb: 2,
                                                        background: darkMode ? "#1a1a2d" : "#ffffff",
                                                        borderColor: darkMode ? "#444" : "#e8e8e8",
                                                    }}
                                                >
                                                    <Grid container justifyContent="space-between" alignItems="center" mb={1.5}>
                                                        <Grid item>
                                                            <MDTypography variant="button" fontWeight="bold" display="inline-flex" alignItems="center" gap={1}>
                                                                Acordo #{acordo.idAcordo || acordo.id}
                                                                <MDBadge
                                                                    badgeContent={acordo.situacao === 1 ? "Ativo" : acordo.situacao === 3 ? "Quitado" : "Cancelado"}
                                                                    color={acordo.situacao === 1 ? "info" : acordo.situacao === 3 ? "success" : "secondary"}
                                                                    variant="gradient"
                                                                    size="xs"
                                                                />
                                                            </MDTypography>
                                                        <MDBox display="flex" gap={2} mt={0.25}>
                                                            <MDTypography variant="caption" color="text">
                                                                <strong>Formalizado:</strong> {acordo.dataFormalizacao ? moment(acordo.dataFormalizacao).format("DD/MM/YYYY") : "-"}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text">
                                                                <strong>Valor Original:</strong> {toCurrency(acordo.valorOriginalTotal)}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text">
                                                                <strong>Valor Acordo:</strong> {toCurrency(acordo.valorAcordoTotal)}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item>
                                                        {pendingParcelas.length > 0 && (
                                                            <MDBox display="flex" alignItems="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={allSelected}
                                                                    indeterminate={someSelected}
                                                                    onChange={(e) => handleToggleSelectAllAcordo(acordo, e.target.checked)}
                                                                />
                                                                <MDTypography variant="caption" fontWeight="medium">
                                                                    Selecionar Tudo (Acordo)
                                                                </MDTypography>
                                                            </MDBox>
                                                        )}
                                                    </Grid>
                                                </Grid>

                                                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${darkMode ? "#444" : "#eee"}` }}>
                                                    <Table size="small">
                                                        <TableHead sx={{ display: "table-header-group", bgcolor: darkMode ? "#2b2b40" : "#f8f9fa" }}>
                                                            <TableRow>
                                                                <TableCell align="center" width="40" sx={{ py: 0.5, px: 1 }}></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Parcela</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Vencimento</MDTypography></TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Valor</MDTypography></TableCell>
                                                                <TableCell align="center" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Situação</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Forma de Pagamento</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Linha Digitável / PIX</MDTypography></TableCell>
                                                                <TableCell align="center" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Ações</MDTypography></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {acordo.parcelas && acordo.parcelas.map(p => {
                                                                const isPending = p.situacao === 1 || p.situacao === 3;
                                                                return (
                                                                    <TableRow key={p.id} hover={isPending}>
                                                                        <TableCell align="center" sx={{ py: 0.25, px: 1 }}>
                                                                            {isPending && (
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    checked={!!selectedItems[p.id]}
                                                                                    onChange={() => handleToggleItem(p.id, p, false, acordo)}
                                                                                />
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{p.numeroParcela} / {acordo.qtdParcelas}</MDTypography></TableCell>
                                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{moment(p.vencimento).format("DD/MM/YYYY")}</MDTypography></TableCell>
                                                                        <TableCell align="right" sx={{ py: 0.5, px: 1, fontWeight: "bold" }}><MDTypography variant="caption" fontWeight="bold">{toCurrency(p.valorParcela)}</MDTypography></TableCell>
                                                                        <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                                                                            <MDBadge
                                                                                badgeContent={p.situacao === 1 ? "Aberto" : p.situacao === 2 ? "Pago" : "Atrasado"}
                                                                                color={p.situacao === 1 ? "info" : p.situacao === 2 ? "success" : "warning"}
                                                                                variant="contained"
                                                                                size="xs"
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell sx={{ py: 0.5, px: 1 }}>
                                                                            <MDTypography variant="caption">
                                                                                {p.situacao === 2 ? (p.formaPagamento || "Manual") : "-"}
                                                                            </MDTypography>
                                                                        </TableCell>
                                                                        <TableCell style={{ fontSize: 10 }} sx={{ py: 0.5, px: 1 }}>
                                                                            <MDTypography variant="caption" style={{ fontSize: 10 }}>{p.linhaDigitavel || p.codigoPix || "-"}</MDTypography>
                                                                        </TableCell>
                                                                        <TableCell align="center" sx={{ py: 0.25, px: 1 }}>
                                                                            {p.situacao === 2 && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="error"
                                                                                    onClick={() => handleEstornar(p.id, false)}
                                                                                    title="Estornar Pagamento"
                                                                                >
                                                                                    <Icon fontSize="small">settings_backup_restore</Icon>
                                                                                </IconButton>
                                                                            )}
                                                                            {p.situacao !== 2 && p.situacao !== 4 && acordo.formaPagamento === 'Boleto' && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="info"
                                                                                    onClick={() => handleEmitirBoleto(p.id)}
                                                                                    title="Emitir 2ª Via do Boleto (Vá em Pessoa > Acordos para atualizar juros/data)"
                                                                                >
                                                                                    <Icon fontSize="small">print</Icon>
                                                                                </IconButton>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Card>
                                        );
                                    })
                                )}
                            </MDBox>
                        )}

                        {/* Contratos Content Area */}
                        {activeTab === "contratos" && (
                            <MDBox mt={2}>
                                {(!activeContratos || activeContratos.length === 0) ? (
                                    <MDTypography variant="button" color="text" display="block" py={2} textAlign="center">
                                        Nenhum contrato em aberto encontrado para este devedor.
                                    </MDTypography>
                                ) : (
                                    <Card variant="outlined" sx={{ p: 1.5, background: darkMode ? "#1a1a2d" : "#ffffff", borderColor: darkMode ? "#444" : "#eee" }}>
                                        {activeContratos.some(c => c.situacao !== 3) && (
                                            <MDBox display="flex" justifyContent="flex-end" mb={1.5} alignItems="center">
                                                <Checkbox
                                                    size="small"
                                                    checked={activeContratos.length > 0 && activeContratos.filter(c => c.situacao !== 3).every(c => selectedItems[c.id])}
                                                    indeterminate={activeContratos.filter(c => c.situacao !== 3).some(c => selectedItems[c.id]) && !activeContratos.filter(c => c.situacao !== 3).every(c => selectedItems[c.id])}
                                                    onChange={(e) => handleToggleSelectAllContratos(e.target.checked)}
                                                />
                                                <MDTypography variant="caption" fontWeight="medium" sx={{ cursor: "pointer", userSelect: "none" }}>
                                                    Selecionar Todos os Contratos
                                                </MDTypography>
                                            </MDBox>
                                        )}
 
                                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${darkMode ? "#444" : "#eee"}` }}>
                                            <Table size="small">
                                                <TableHead sx={{ display: "table-header-group", bgcolor: darkMode ? "#2b2b40" : "#f8f9fa" }}>
                                                    <TableRow>
                                                        <TableCell align="center" width="40" sx={{ py: 0.5, px: 1 }}></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Título</MDTypography></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Parcela</MDTypography></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Vencimento</MDTypography></TableCell>
                                                        <TableCell align="right" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Valor Original</MDTypography></TableCell>
                                                        <TableCell align="right" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Saldo Atual</MDTypography></TableCell>
                                                        <TableCell align="center" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Situação</MDTypography></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Forma Pagto</MDTypography></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Carteira</MDTypography></TableCell>
                                                        <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Processo</MDTypography></TableCell>
                                                        <TableCell align="center" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Ações</MDTypography></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {activeContratos.map(c => {
                                                        const isPending = c.situacao === null || c.situacao === undefined || c.situacao === 1 || c.situacao === 2;
                                                        const isPaidOrPartial = c.situacao === 2 || c.situacao === 3;
                                                        return (
                                                            <TableRow key={c.id} hover={isPending}>
                                                                <TableCell align="center" sx={{ py: 0.25, px: 1 }}>
                                                                    {isPending && (
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={!!selectedItems[c.id]}
                                                                            onChange={() => handleToggleItem(c.id, c, true)}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{c.titulo}</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{c.parcela || "-"}</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{c.vencimento ? moment(c.vencimento).format("DD/MM/YYYY") : "-"}</MDTypography></TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{toCurrency(c.valorOriginal)}</MDTypography></TableCell>
                                                                <TableCell align="right" sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>
                                                                    <MDTypography variant="caption" fontWeight="bold" color={c.saldo > 0 ? "error" : "success"}>
                                                                        {toCurrency(c.saldo)}
                                                                    </MDTypography>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                                                                    <MDBadge
                                                                        badgeContent={c.situacao === 3 ? "Liquidado" : c.situacao === 2 ? "Baixado Parcial" : "Aberto"}
                                                                        color={c.situacao === 3 ? "success" : c.situacao === 2 ? "warning" : "info"}
                                                                        variant="contained"
                                                                        size="xs"
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}>
                                                                    <MDTypography variant="caption">
                                                                        {isPaidOrPartial ? (c.formaPagamento || "Manual") : "-"}
                                                                    </MDTypography>
                                                                </TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{c.nomeCarteira || "-"}</MDTypography></TableCell>
                                                                <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{c.numeroProcesso || "-"}</MDTypography></TableCell>
                                                                <TableCell align="center" sx={{ py: 0.25, px: 1 }}>
                                                                    {isPaidOrPartial && (
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleEstornar(c.id, true)}
                                                                            title="Estornar Pagamento"
                                                                        >
                                                                            <Icon fontSize="small">settings_backup_restore</Icon>
                                                                        </IconButton>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Card>
                                )}
                            </MDBox>
                        )}
                    </Card>
                </Grid>
            )}

            {/* Bottom Actions Floating Bar */}
            {selectedCount > 0 && (
                <Grid item xs={12} style={{ position: "sticky", bottom: 12, zIndex: 100 }}>
                    <Card
                        variant="elevation"
                        elevation={4}
                        sx={{
                            px: 2,
                            py: 1,
                            background: darkMode ? "linear-gradient(90deg, #1f1f3a, #111126)" : "linear-gradient(90deg, #3f51b5, #1a237e)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: "8px",
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)"
                        }}
                    >
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="medium" style={{ color: "#4caf50" }}>check_circle</Icon>
                            <MDTypography variant="button" color="white" fontWeight="bold">
                                {selectedCount} {activeTab === "acordos" ? "parcela(s)" : "contrato(s)"} selecionado(s) para baixa
                            </MDTypography>
                        </MDBox>
                        <MDButton
                            variant="gradient"
                            color="success"
                            size="small"
                            onClick={handleOpenBaixaDialog}
                            sx={{
                                px: 3,
                                textTransform: "uppercase",
                                fontWeight: "bold"
                            }}
                        >
                            <Icon sx={{ mr: 0.5 }}>price_check</Icon> Baixar Selecionados
                        </MDButton>
                    </Card>
                </Grid>
            )}

            {/* Batch Baixa Dialog */}
            <Dialog open={baixaDialogOpen} onClose={handleCloseBaixaDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pb: 0.5, bgcolor: darkMode ? "#111" : "#f8f9fa" }}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="success" size="small">price_check</Icon>
                        <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: "14px" }}>
                            Baixa Manual em Lote ({activeTab === "acordos" ? "Parcelas de Acordo" : "Contratos"})
                        </MDTypography>
                    </MDBox>
                </DialogTitle>
                <DialogContent sx={{ mt: 1 }}>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.25, pt: 0.2 }}>Data do Pagamento</InputLabel>
                            <MDInput
                                type="date"
                                size="small"
                                value={baixaDataPagamento}
                                onChange={(e) => setBaixaDataPagamento(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.25, pt: 0.2 }}>Forma de Pagamento</InputLabel>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={baixaFormaPagamento}
                                    onChange={(e) => setBaixaFormaPagamento(e.target.value)}
                                    sx={{ height: "34.125px" }}
                                >
                                    <MenuItem value="Boleto">Boleto</MenuItem>
                                    <MenuItem value="PIX">PIX</MenuItem>
                                    <MenuItem value="Depósito">Depósito</MenuItem>
                                    <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                                    <MenuItem value="Transferência">Transferência</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                        <Table size="small">
                            <TableHead sx={{ display: "table-header-group", bgcolor: darkMode ? "#222" : "#f5f5f5" }}>
                                <TableRow>
                                    <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Item</MDTypography></TableCell>
                                    <TableCell align="right" width="120" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Valor Aberto</MDTypography></TableCell>
                                    <TableCell align="right" width="120" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Desconto (R$)</MDTypography></TableCell>
                                    <TableCell align="right" width="120" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Acréscimo (R$)</MDTypography></TableCell>
                                    <TableCell align="right" width="140" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="text">Valor Pago (R$)</MDTypography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(baixaForm).map(key => {
                                    const formItem = baixaForm[key];
                                    return (
                                        <TableRow key={key}>
                                            <TableCell sx={{ py: 0.5, px: 1 }}>
                                                <MDTypography variant="caption" fontWeight="medium" display="block">
                                                    {formItem.title}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell align="right" width="120" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption">{toCurrency(formItem.balance)}</MDTypography></TableCell>
                                            <TableCell align="right" width="120" sx={{ py: 0.25, px: 1 }}>
                                                <MDInput
                                                    size="small"
                                                    type="number"
                                                    value={formItem.valorDesconto}
                                                    onChange={(e) => handleFormValueChange(key, "valorDesconto", e.target.value)}
                                                    sx={{ width: 90, "& input": { textAlign: "right", fontSize: "12px", py: 0.5 } }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" width="120" sx={{ py: 0.25, px: 1 }}>
                                                <MDInput
                                                    size="small"
                                                    type="number"
                                                    value={formItem.valorAcrescimo}
                                                    onChange={(e) => handleFormValueChange(key, "valorAcrescimo", e.target.value)}
                                                    sx={{ width: 90, "& input": { textAlign: "right", fontSize: "12px", py: 0.5 } }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" width="140" sx={{ py: 0.25, px: 1 }}>
                                                <MDInput
                                                    size="small"
                                                    type="number"
                                                    value={formItem.valorPago}
                                                    onChange={(e) => handleFormValueChange(key, "valorPago", e.target.value)}
                                                    sx={{ width: 110, "& input": { textAlign: "right", fontSize: "12px", py: 0.5 } }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {/* Totals Summary Row */}
                                <TableRow sx={{ bgcolor: darkMode ? "#1a1a2b" : "#fcfcfc", "& td": { fontWeight: "bold" } }}>
                                    <TableCell sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold">Total Geral</MDTypography></TableCell>
                                    <TableCell align="right" width="120" sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold">{toCurrency(totals.totalOriginal)}</MDTypography></TableCell>
                                    <TableCell align="right" width="120" style={{ color: "#2196f3" }} sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="info">{toCurrency(totals.totalDesconto)}</MDTypography></TableCell>
                                    <TableCell align="right" width="120" style={{ color: "#ff9800" }} sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="warning">{toCurrency(totals.totalAcrescimo)}</MDTypography></TableCell>
                                    <TableCell align="right" width="140" style={{ color: "#4caf50" }} sx={{ py: 0.5, px: 1 }}><MDTypography variant="caption" fontWeight="bold" color="success">{toCurrency(totals.totalPago)}</MDTypography></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: darkMode ? "#111" : "#f8f9fa" }}>
                    <MDButton onClick={handleCloseBaixaDialog} color="secondary">Cancelar</MDButton>
                    <MDButton
                        onClick={() => setConfirmDialogOpen(true)}
                        color="success"
                        variant="gradient"
                        disabled={loading || totals.totalPago <= 0}
                    >
                        <Icon sx={{ mr: 1 }}>check_circle</Icon> Confirmar Baixas
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1, bgcolor: darkMode ? "#111" : "#f8f9fa" }}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="warning" size="medium">warning</Icon>
                        <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: "14px" }}>
                            Validar Baixa em Lote
                        </MDTypography>
                    </MDBox>
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <MDTypography variant="button" color="text" sx={{ mb: 2, display: "block" }}>
                        Confirma a realização da baixa para o(s) <strong>{selectedCount}</strong> {activeTab === "acordos" ? "item(ns) de acordo" : "contrato(s)"}?
                    </MDTypography>
                    <MDBox sx={{ bgcolor: darkMode ? "#222" : "#f5f5f5", p: 2, borderRadius: 1, mb: 1 }}>
                        <MDBox display="flex" justifyContent="space-between" mb={1}>
                            <MDTypography variant="caption" color="text">Data do Pagamento:</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold">{baixaDataPagamento ? moment(baixaDataPagamento).format("DD/MM/YYYY") : "-"}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" mb={1}>
                            <MDTypography variant="caption" color="text">Forma de Pagamento:</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold">{baixaFormaPagamento}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" mb={1}>
                            <MDTypography variant="caption" color="text">Total Aberto:</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold">{toCurrency(totals.totalOriginal)}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" mb={1}>
                            <MDTypography variant="caption" color="text">Total Desconto:</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold" color="info">{toCurrency(totals.totalDesconto)}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" mb={1}>
                            <MDTypography variant="caption" color="text">Total Acréscimo:</MDTypography>
                            <MDTypography variant="caption" fontWeight="bold" color="warning">{toCurrency(totals.totalAcrescimo)}</MDTypography>
                        </MDBox>
                        <Divider sx={{ my: 1 }} />
                        <MDBox display="flex" justifyContent="space-between">
                            <MDTypography variant="button" fontWeight="bold">Total Pago:</MDTypography>
                            <MDTypography variant="button" fontWeight="bold" color="success">{toCurrency(totals.totalPago)}</MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: darkMode ? "#111" : "#f8f9fa" }}>
                    <MDButton onClick={() => setConfirmDialogOpen(false)} color="secondary">Voltar</MDButton>
                    <MDButton
                        onClick={handleConfirmBaixa}
                        color="success"
                        variant="gradient"
                        disabled={loading}
                    >
                        {loading ? "Processando..." : "Sim, Confirmar"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Snackbars */}
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </Grid>
    );
}
